import { useTranslation } from 'react-i18next'
import { CalendarClock, Info } from 'lucide-react'
import { Card, CardContent, CardIcon, type CardSurface } from '@/components/ui/card'
import { ListCardHeader } from '@/components/ListCardHeader'
import {
  CampaignTimeline,
  PacingBadge,
  flightStateOf,
  pacingOf,
} from '@/components/CampaignTimeline'
import {
  formatObjectiveValue,
  primaryObjective,
  type DeliveryCurve,
  type LineItemObjectives,
} from '@/services/lineItems/lineItemsApi'
import { formatDate } from '../utils/format'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

/** A figure over its label — the card's unit of reading. */
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-(--ad-text-muted)">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-(--ad-text)">{value}</span>
    </div>
  )
}

export interface DeliveryProgressCardProps {
  startDate: string
  endDate: string
  executionState: string
  deliveryPct?: number | null
  elapsedTimePct?: number | null
  objectives?: LineItemObjectives
  /** How much of each objective has been delivered so far. */
  objectiveDelivery?: LineItemObjectives
  /**
   * How the line item paces. It decides what the bar is supposed to look like,
   * so the card says as much rather than leaving a reader to score delivery
   * against the tick as if every campaign paced to the clock.
   */
  deliveryCurve?: DeliveryCurve
  /** `sunken` for a read-only card beside a form; see {@link CardSurface}. */
  surface?: CardSurface
}

/**
 * Delivery against the flight, as the list shows it in a cell but with the
 * numbers written out: how much has been delivered, how much of the flight has
 * passed, and how long is left. The same bar and the same pacing verdict as
 * the line item list, so a campaign reads the same in both places.
 */
export function DeliveryProgressCard({
  startDate,
  endDate,
  executionState,
  deliveryPct,
  elapsedTimePct,
  objectives,
  objectiveDelivery,
  deliveryCurve,
  surface,
}: DeliveryProgressCardProps) {
  const { t } = useTranslation()
  const delivery = deliveryPct ?? null
  const flight = flightStateOf(startDate, endDate, executionState, elapsedTimePct ?? null)
  const pacing = pacingOf(flight, delivery)

  const objective = primaryObjective(objectives)
  const delivered = objective ? objectiveDelivery?.[objective.type] : undefined

  const fmtDate = (value: string) =>
    value
      ? formatDate(value, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—'

  // Days while there are days left, hours once it is down to the last one —
  // "0 days left" would read as ended when the campaign is still delivering.
  const timeLeft =
    flight.future && flight.start
      ? t('pacing.startsIn', {
          count: Math.max(1, Math.ceil((flight.start.getTime() - Date.now()) / DAY_MS)),
        })
      : flight.msLeft === null
        ? t('pacing.ended')
        : flight.msLeft >= DAY_MS
          ? t('pacing.daysLeft', { count: Math.ceil(flight.msLeft / DAY_MS) })
          : t('pacing.hoursLeft', { count: Math.max(1, Math.ceil(flight.msLeft / HOUR_MS)) })

  return (
    <Card surface={surface}>
      <ListCardHeader
        title={
          <>
            <CardIcon icon={CalendarClock} />
            {t('pacing.title')}
          </>
        }
        actions={<PacingBadge pacing={pacing} flight={flight} />}
      />
      <CardContent className="block">

        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <span className="text-lg font-semibold tabular-nums text-(--ad-text)">
            {delivery === null ? '—' : `${Math.round(delivery)}%`}
          </span>
          <span className="text-xs text-(--ad-text-muted)">
            {t('pacing.timeElapsed', { value: flight.elapsed ? 100 : Math.round(flight.timePct) })}
          </span>
        </div>

        <CampaignTimeline
          startDate={startDate}
          endDate={endDate}
          executionState={executionState}
          deliveryPct={delivery}
          elapsedTimePct={elapsedTimePct ?? null}
          size="md"
        />

        <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-(--ad-text-muted)">
          <span>{fmtDate(startDate)}</span>
          <span>{fmtDate(endDate)}</span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-3 sm:grid-cols-3">
          {flight.ended ? (
            <Figure
              label={flight.elapsed ? t('pacing.endedOn') : t('pacing.wasDueToEnd')}
              value={fmtDate(endDate)}
            />
          ) : (
            <Figure label={t('pacing.timeRemaining')} value={timeLeft} />
          )}
          {objective && (
            <>
              <Figure
                label={t(`lineItems.objectiveTypes.${objective.type}`, {
                  defaultValue: objective.type,
                })}
                value={
                  delivered != null ? formatObjectiveValue(delivered) : t('pacing.nothingDelivered')
                }
              />
              <Figure label={t('pacing.target')} value={formatObjectiveValue(objective.target)} />
            </>
          )}
        </div>

        {/* What the curve means for the bar above — a campaign pacing ahead of
            the clock is not over-delivering, and one tracking it is not late. */}
        {deliveryCurve && !flight.ended && (
          <div className="mt-3 flex items-start gap-1.5 text-xs text-(--ad-text-muted)">
            <Info className="mt-px size-3.5 shrink-0" />
            <span>
              <span className="font-semibold">
                {t(`lineItems.deliveryCurves.${deliveryCurve}`, { defaultValue: deliveryCurve })}
              </span>
              {' — '}
              {t(`pacing.curveNotes.${deliveryCurve}`, { defaultValue: '' })}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
